/**
 * offlineAuthFetch – Drop-in replacement für authFetch mit Offline-Support.
 * 
 * GET:   Versucht fetch, bei Fehler → cached data aus IndexedDB
 * POST/PATCH/DELETE: Bei offline → Queue in IndexedDB + optimistischer Return
 */
import { addToQueue, setCachedData, getCachedData, getPendingCount } from "./offlineDB";
import { processQueue } from "./offlineSync";

/**
 * @param {string} url - API endpoint (e.g. /api/clock/in)
 * @param {object} options - fetch options
 * @param {object} offlineOpts - { type, cacheKey, optimisticResponse, meta }
 */
export async function offlineAuthFetch(url, options = {}, offlineOpts = {}) {
  const token = localStorage.getItem("token");
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const method = (options.method || "GET").toUpperCase();
  const isRead = method === "GET";
  const cacheKey = offlineOpts.cacheKey || url;

  // ── READ requests ──
  if (isRead) {
    try {
      const response = await fetch(url, { ...options, headers });
      if (response.ok) {
        // Clone and cache the response
        const clone = response.clone();
        try {
          const data = await clone.json();
          await setCachedData(cacheKey, data);
        } catch {
          // non-JSON response, skip caching
        }
      }
      if (response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
      return response;
    } catch {
      // Network error → return cached data
      const cached = await getCachedData(cacheKey);
      if (cached !== null) {
        return new Response(JSON.stringify(cached), {
          status: 200,
          headers: { "Content-Type": "application/json", "X-Offline-Cache": "true" },
        });
      }
      // No cache available
      return new Response(JSON.stringify({ detail: "Offline – keine gecachten Daten" }), {
        status: 503,
        headers: { "Content-Type": "application/json" },
      });
    }
  }

  // ── WRITE requests ──
  try {
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return response;
  } catch {
    // Network error → queue for later sync
    if (isFormData) {
      // FormData can't be serialized to IndexedDB easily, skip queue
      return new Response(JSON.stringify({ detail: "Offline – Foto wird später hochgeladen" }), {
        status: 202,
        headers: { "Content-Type": "application/json", "X-Offline-Queued": "true" },
      });
    }

    const body = options.body ? (typeof options.body === "string" ? JSON.parse(options.body) : options.body) : null;

    await addToQueue({
      type: offlineOpts.type || "generic",
      endpoint: url,
      method,
      body,
      meta: offlineOpts.meta || {},
    });

    // Trigger sync check in background
    setTimeout(() => { if (navigator.onLine) processQueue(); }, 1000);

    // Return optimistic response
    const optimistic = offlineOpts.optimisticResponse || { queued: true, offline: true };
    return new Response(JSON.stringify(optimistic), {
      status: 200,
      headers: { "Content-Type": "application/json", "X-Offline-Queued": "true" },
    });
  }
}
